import { resetFullMessengerPreload } from "./full-messenger-preload";
import {
  callIntercom,
  hasIntercomSession,
  intercomReady,
} from "./intercom-sdk";
import { syncLauncherVisibility } from "./launcher-visibility";
import {
  resetMessengerState,
  setMessengerOpen,
  setUnreadCount,
} from "./messenger-state";
import { resetPassiveNotifications } from "./passive-notifications";

const IntercomAppSettings = {
  app_id: import.meta.env.VITE_INTERCOM_APP_ID,
  api_base: "https://api-iam.intercom.io",
};

/**
 * Intercom rejects a `shutdown` immediately followed by a `boot`; the session
 * teardown has to land first or the new one inherits the old contact.
 */
const IDENTITY_SWAP_DELAY_MS = 300;

/** The user the messenger is currently booted for, or null while anonymous. */
let identifiedUserId: string | null = null;

/**
 * Serialized attributes of the last `update`, so repeated renders carrying
 * identical data do not spend the update budget — Intercom throttles past 20
 * calls per 30 minutes.
 */
let lastIdentityPayload: string | null = null;

/**
 * Intercom drops callbacks registered before a boot, so every boot has to
 * register them again — verified against the live widget.
 */
function registerMessengerCallbacks() {
  callIntercom("onShow", () => {
    setMessengerOpen(true);
    // Opening from "Chat with us" must bring the launcher with it: while the
    // panel is open the launcher is its minimise control.
    syncLauncherVisibility();
  });
  callIntercom("onHide", () => {
    setMessengerOpen(false);
    syncLauncherVisibility();
  });
  callIntercom("onUnreadCountChange", (count: unknown) => {
    setUnreadCount(typeof count === "number" ? count : 0);
    syncLauncherVisibility();
  });
}

function bootMessenger(attributes: Record<string, unknown>) {
  callIntercom("boot", {
    ...IntercomAppSettings,
    ...attributes,
    // Always mounted, never hidden by Intercom — see launcher-visibility.
    hide_default_launcher: false,
  });
  registerMessengerCallbacks();
  syncLauncherVisibility();
}

let identityQueue: Promise<void> = Promise.resolve();

/**
 * Publishes the current user to Intercom.
 *
 * Attribute changes go through `update`: `shutdown` + `boot` would close an
 * open messenger mid-conversation, and while it is shut down `show()` is a
 * silent no-op, which is what left the "Chat with us" button dead (DW-2092).
 * Intercom documents `shutdown` for logout and user switching only.
 */
export function identify(attributes: Record<string, unknown>): Promise<void> {
  // Serialized: a burst of changes must not interleave an update with the boot
  // it belongs after, which would leave Intercom holding the older attributes.
  const run = identityQueue.then(() => applyIdentity(attributes));
  identityQueue = run.catch(() => undefined);
  return run;
}

async function applyIdentity(attributes: Record<string, unknown>) {
  const userId = (attributes.user_id as string | undefined) ?? null;
  const payload = JSON.stringify(attributes);
  if (identifiedUserId === userId && payload === lastIdentityPayload) return;

  await intercomReady();

  if (identifiedUserId === userId) {
    lastIdentityPayload = payload;
    callIntercom("update", attributes);
    return;
  }

  // A page load is a fresh start, not a user switch. Module state is empty on
  // every load, so `identifiedUserId === null` says nothing about who Intercom
  // already has — and its cookie normally still holds this same user. Tearing
  // that down took the conversation it was restoring with it, which is where
  // http_post_session_empty came from. Only a genuine change of user needs the
  // teardown, which is the case Intercom documents shutdown for.
  const isUserSwitch = identifiedUserId !== null;
  identifiedUserId = userId;
  lastIdentityPayload = payload;

  if (!isUserSwitch && hasIntercomSession()) {
    // Already identified from a previous page: only the attributes are new.
    callIntercom("update", attributes);
    registerMessengerCallbacks();
    syncLauncherVisibility();
    return;
  }

  if (isUserSwitch) {
    callIntercom("shutdown");
    resetMessengerState();
    resetPassiveNotifications();
    resetFullMessengerPreload();
    await new Promise((resolve) => setTimeout(resolve, IDENTITY_SWAP_DELAY_MS));
  }
  bootMessenger(attributes);
}
